import { redirectToLogin } from "../services/httpClient";
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  ClickAwayListener,
} from '@mui/material';
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import DateRangeFilter from "../components/DateRangeFilter";
import { titleCaseName } from "../utils/nameCase";
import MyEvolutionsWidget from "../components/Dashboard/MyEvolutionsWidget";
import CancellationsSection from "../components/Dashboard/CancellationsSection";
import { CalendarToday, CheckCircle, Assignment, Cake, BarChart, InfoOutlined, ViewKanban, Star } from '@mui/icons-material';
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { colors } from '../theme/designSystem';
import { dashboardService, DashboardSummary } from "../services/dashboardService";
import { useAuth } from "../contexts/AuthContext";
import {
  opportunitiesService,
  OpportunitySummary,
  UrgentOpportunityItem,
  OPPORTUNITY_TYPE_LABELS,
} from "../services/opportunitiesService";
import { crmService, PipelineSummary } from "../services/crmService";

interface MenuItemProps { label: string; href?: string; onClick?: (e: React.MouseEvent) => void; }

interface UserSession {
  email: string;
  alias: string;
  clinicName: string;
  role: string;
  permissions: string[];
  menuItems: MenuItemProps[];
  loginTime: string;
}

// Donut SVG data-driven (padrão do sistema): segmentos proporcionais aos valores.
const Donut: React.FC<{ segments: { value: number; color: string }[]; centerValue: number }> = ({ segments, centerValue }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  return (
    <Box sx={{ position: 'relative', width: '120px', height: '120px', flexShrink: 0 }}>
      <svg width="120" height="120" viewBox="0 0 42 42" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
        {segments.map((seg, i) => {
          const pct = (seg.value / total) * 100;
          if (pct <= 0) return null;
          const el = (
            <circle key={i} cx="21" cy="21" r="15.915" fill="transparent" stroke={seg.color} strokeWidth="3"
              strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={-offset} strokeLinecap="round" />
          );
          offset += pct;
          return el;
        })}
      </svg>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2196f3' }}>{centerValue}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total</Typography>
      </Box>
    </Box>
  );
};

const LegendRow: React.FC<{ color: string; label: string; value: number }> = ({ color, label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '50%', flexShrink: 0 }} />
      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, lineHeight: 1.3 }}>{label}</Typography>
    </Box>
    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color, whiteSpace: 'nowrap' }}>{value}</Typography>
  </Box>
);

// Dois botões lado a lado no mesmo cartão: fonte e respiro menores para "VER AGENDA" caber numa linha
// no Firefox a 100% (achado do Hilton, 07/09).
const pairButtonSx = {
  backgroundColor: '#03B4C6', color: 'white', padding: '0.5rem 0.5rem', fontSize: '0.78rem', letterSpacing: 0,
  fontWeight: 600, textTransform: 'none' as const, borderRadius: '8px', boxShadow: 'none', whiteSpace: 'nowrap' as const, minWidth: 0,
  '&:hover': { backgroundColor: '#029AAB', boxShadow: 'none' },
};

const cardButtonSx = {
  backgroundColor: '#03B4C6', color: 'white', padding: '0.5rem 1rem', fontSize: '0.875rem',
  fontWeight: 600, textTransform: 'none' as const, borderRadius: '8px', boxShadow: 'none',
  '&:hover': { backgroundColor: '#029AAB', boxShadow: 'none' },
};

// Explicação da lógica do painel. Abre no hover (desktop) E no clique (celular, onde não existe hover) —
// exigência de responsividade: informação não pode ficar acessível só por passagem de mouse.
const PanelInfo: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        arrow
        placement="top"
        open={open}
        title={text}
        onClose={() => setOpen(false)}
        slotProps={{ tooltip: { sx: { fontSize: '0.8rem', lineHeight: 1.5, maxWidth: 340, p: 1.5 } } }}
      >
        <IconButton
          aria-label="Como este painel é calculado"
          size="small"
          onClick={() => setOpen((v) => !v)}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          sx={{ ml: 'auto', color: colors.textSecondary, flexShrink: 0 }}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  );
};

// Datas locais chegam sem offset ("2026-08-05T10:00:00"): formatar por partes, sem new Date(iso)
const fmtDay = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const fmtDayTime = (iso: string) => `${fmtDay(iso)} ${iso.slice(11, 16)}`;
const WEEKDAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const weekdayOf = (iso: string) =>
  WEEKDAY_SHORT[new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))).getDay()];

const urgentPhrase = (u: UrgentOpportunityItem): string => {
  const when = u.candidateDate ? fmtDay(u.candidateDate) : '';
  if (u.type === 'holiday_relocation') {
    return `${u.patientName} tem sessão marcada em feriado (${when}) — a sessão será perdida se não for realocada. Oferecer esta vaga.`;
  }
  if (u.type === 'double_session') {
    return `${u.patientName} já estará na clínica no dia da vaga — oferecer sessão extra (dobradinha).`;
  }
  return `${u.patientName} tem sessão em ${when} — oferecer antecipação para esta vaga.`;
};

// Duas oportunidades no widget: emparelha a altura com a coluna de KPIs ao lado (a fila completa fica no CTA).
const MAX_URGENT_OPPORTUNITIES = 2;
const MAX_PIPELINE_CARDS = 2;

const money = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

// Widget do motor de oportunidades (RN-409): KPIs + urgentes; CTA abre a fila de trabalho.
const OpportunitiesWidget: React.FC<{ onOpen: () => void; onOpenRepositions: () => void; onUnavailable: () => void }> = ({ onOpen, onOpenRepositions, onUnavailable }) => {
  const { user } = useAuth();
  const [data, setData] = useState<OpportunitySummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user?.entityId) return;
    opportunitiesService.getSummary(user.entityId)
      .then(setData)
      .catch(() => { setFailed(true); onUnavailable(); });
  }, [user?.entityId, onUnavailable]);

  // sem permissão da página / erro: o widget some sem quebrar o dashboard
  if (failed || !data) return null;

  const kpis = [
    { value: data.openVacancies, label: 'Vagas abertas por cancelamento', highlight: data.openVacancies > 0 },
    { value: data.holidaySessions, label: 'Sessões caindo em feriado', highlight: false },
    { value: money(data.revenueAtRisk), label: 'Receita em risco', highlight: false },
  ];

  return (
    <Paper elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important', p: 3, borderRadius: '12px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#0d6e6e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Assignment fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>Oportunidades de agenda</Typography>
          <PanelInfo text={'Vagas que abriram na agenda dos próximos 15 dias e podem ser reaproveitadas. "Vagas abertas por cancelamento" são horários que ficaram livres; "sessões caindo em feriado" serão perdidas se não forem realocadas; "receita em risco" é quanto essas sessões valem se ninguém agir. Ao lado ficam as duas oportunidades de maior score — a nota considera o tipo (feriado, dobradinha, antecipação), quantos dias se ganha e o vínculo do paciente com o profissional. Use "Ver oportunidades e agir" para abrir a fila completa.'} />
        </Box>
        <Typography variant="caption" sx={{ bgcolor: '#e7f1ff', color: '#0a58ca', px: 1.5, py: 0.5, borderRadius: '999px', fontWeight: 600 }}>
          próximos 15 dias
        </Typography>
      </Box>

      {data.staleCount > 0 && (
        <Box sx={{ backgroundColor: '#f8d7da', color: '#842029', borderRadius: '8px', px: 2, py: 1, mb: 2, fontSize: '0.85rem', fontWeight: 600 }}>
          {data.staleCount} oportunidade{data.staleCount > 1 ? 's' : ''} sem ação há mais de 24h
        </Box>
      )}

      {/* RN-409f: dívida da clínica com o paciente — clica e cai direto na aba Reposições */}
      {(data.repositionsPending ?? 0) > 0 && (
        <Box
          onClick={onOpenRepositions}
          sx={{ backgroundColor: '#d1e7dd', color: '#0f5132', borderRadius: '8px', px: 2, py: 1, mb: 2, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', '&:hover': { backgroundColor: '#bcd9c8' } }}
        >
          {data.repositionsPending} reposi{(data.repositionsPending ?? 0) > 1 ? 'ções' : 'ção'} a combinar — o profissional cancelou e a clínica deve a sessão →
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, alignContent: 'start' }}>
          {kpis.map((k, i) => (
            <Box key={i} sx={{ border: '1px solid #e9ecef', borderRadius: '8px', p: 1.5 }}>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: k.highlight ? '#dc3545' : colors.textPrimary, lineHeight: 1.2 }}>
                {k.value}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{k.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
          {data.urgent.length === 0 && (data.urgentRepositions ?? []).length === 0 ? (
            <Box sx={{ border: '1px dashed #ced4da', borderRadius: '8px', p: 2, textAlign: 'center', color: colors.textSecondary, fontSize: '0.9rem' }}>
              Nenhuma ação pendente — vagas e reposições em dia.
            </Box>
          ) : (
            <>
            {data.urgent.slice(0, MAX_URGENT_OPPORTUNITIES).map((u) => (
              <Box key={u.opportunityId} sx={{ border: '1px solid #e9ecef', borderRadius: '8px', p: 1.5 }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 700, letterSpacing: '0.04em' }}>
                  {OPPORTUNITY_TYPE_LABELS[u.type]?.toUpperCase() ?? u.type} · score {Math.round(u.score)}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary }}>
                  {weekdayOf(u.vacancyStartsAt)} {fmtDayTime(u.vacancyStartsAt)}
                  {u.professionalName ? ` · ${u.professionalName}` : ''}
                  {u.serviceName ? ` — ${u.serviceName}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary }}>{urgentPhrase(u)}</Typography>
              </Box>
            ))}
            {/* Reposições mais antigas completam os 2 slots quando há espaço (pedido do
                Hilton, 21/08): sem isso, "0 vagas" dizia "tudo tratado" com dezenas de
                reposições esperando na aba */}
            {(data.urgentRepositions ?? [])
              .slice(0, Math.max(0, MAX_URGENT_OPPORTUNITIES - data.urgent.length))
              .map((r) => (
                <Box
                  key={r.needId}
                  onClick={onOpenRepositions}
                  sx={{ border: '1px solid #e9ecef', borderRadius: '8px', p: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: '#f6faf8' } }}
                >
                  <Typography variant="caption" sx={{ color: '#0f5132', fontWeight: 700, letterSpacing: '0.04em' }}>
                    REPOSIÇÃO · RP-{String(r.number).padStart(7, '0')}
                    {r.daysWaiting > 0 ? ` · aguarda há ${r.daysWaiting} dia${r.daysWaiting > 1 ? 's' : ''}` : ' · de hoje'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary }}>
                    {weekdayOf(r.lostStartsAt)} {fmtDayTime(r.lostStartsAt)}
                    {r.professionalName ? ` · ${r.professionalName}` : ''}
                    {r.serviceName ? ` — ${r.serviceName}` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary }}>
                    {`Sessão de ${r.patientName ?? 'paciente'} cancelada pelo profissional — combinar a reposição.`}
                  </Typography>
                </Box>
              ))}
            </>
          )}
          <Button onClick={onOpen} variant="contained" fullWidth sx={{ ...cardButtonSx, mt: 'auto', backgroundColor: '#0d6e6e', '&:hover': { backgroundColor: '#0a5757' } }}>
            Ver oportunidades e agir →
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};


// Widget do CRM (RN-604): pendencias primeiro — o que doi num pipeline e familia esperando
// resposta. Reusa o mesmo resumo do painel de pipelines, que ja aplica o acesso da RN-603:
// quem nao e responsavel de nenhum pipeline recebe lista vazia e o widget some.
const PipelineWidget: React.FC<{ onOpen: () => void; onOpenPipeline: (id: string) => void; onUnavailable: () => void }> = ({ onOpen, onOpenPipeline, onUnavailable }) => {
  const { user } = useAuth();
  const [data, setData] = useState<PipelineSummary[] | null>(null);

  useEffect(() => {
    if (!user?.entityId) return;
    crmService.getPipelineSummaries(user.entityId)
      .then((list) => {
        if (list.length === 0) { onUnavailable(); return; }
        setData(list);
      })
      .catch(() => onUnavailable());
  }, [user?.entityId, onUnavailable]);

  if (!data) return null;

  const sum = (pick: (p: PipelineSummary) => number) => data.reduce((acc, p) => acc + pick(p), 0);
  const awaitingReply = sum((p) => p.awaitingReply);
  const unassigned = sum((p) => p.unassigned);
  const windowClosing = sum((p) => p.windowClosing);
  const newLast7Days = sum((p) => p.newLast7Days);
  const wonLast30Days = sum((p) => p.wonLast30Days);

  const kpis = [
    { value: awaitingReply, label: 'Leads sem resposta', highlight: awaitingReply > 0 },
    { value: unassigned, label: 'Leads sem responsável', highlight: false },
    { value: windowClosing, label: 'Janela de 24h acabando', highlight: windowClosing > 0 },
  ];

  // primeiro os que precisam de acao; entre eles, o de maior pendencia
  const pending = (p: PipelineSummary) => p.windowClosing + p.awaitingReply + p.unassigned + p.stale;
  const ranked = [...data].sort((a, b) => pending(b) - pending(a));

  return (
    <Paper elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important', p: 3, borderRadius: '12px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#0d6e6e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ViewKanban fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>Pipeline de captação</Typography>
          <PanelInfo text={'Leads que chegaram pelo WhatsApp ou foram cadastrados à mão, por pipeline. "Sem resposta" são conversas em que a última mensagem é do lead e ninguém respondeu; "sem responsável" são leads que ninguém assumiu; "janela de 24h acabando" é a regra da Meta — passadas 24h da última mensagem do contato, a clínica só consegue enviar modelo aprovado, então a conversa esfria. Ao lado, os pipelines que você acessa, o mais pendente primeiro; clique para abrir o quadro. No rodapé, o resultado do período. Você só enxerga os pipelines em que é responsável.'} />
        </Box>
        <Typography variant="caption" sx={{ bgcolor: '#e7f1ff', color: '#0a58ca', px: 1.5, py: 0.5, borderRadius: '999px', fontWeight: 600 }}>
          CRM
        </Typography>
      </Box>

      {windowClosing > 0 && (
        <Box sx={{ backgroundColor: '#f8d7da', color: '#842029', borderRadius: '8px', px: 2, py: 1, mb: 2, fontSize: '0.85rem', fontWeight: 600 }}>
          {windowClosing} conversa{windowClosing > 1 ? 's' : ''} com a janela de 24h acabando — responda antes de precisar de modelo aprovado
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, alignContent: 'start' }}>
          {kpis.map((k, i) => (
            <Box key={i} sx={{ border: '1px solid #e9ecef', borderRadius: '8px', p: 1.5 }}>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: k.highlight ? '#dc3545' : colors.textPrimary, lineHeight: 1.2 }}>
                {k.value}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{k.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
          {ranked.slice(0, MAX_PIPELINE_CARDS).map((p) => (
            <Box
              key={p.id}
              onClick={() => onOpenPipeline(p.id)}
              sx={{ border: '1px solid #e9ecef', borderRadius: '8px', p: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: '#f6faf8' } }}
            >
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 700, letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {p.name.toUpperCase()}
                {p.isDefault && <Star sx={{ fontSize: 13, color: '#f0ad00' }} />}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary }}>
                {p.openLeads} lead{p.openLeads === 1 ? '' : 's'} em aberto
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: pending(p) > 0 ? '#842029' : colors.textSecondary }}>
                {pending(p) === 0
                  ? 'Sem pendências.'
                  : [
                      p.windowClosing > 0 ? `${p.windowClosing} com janela acabando` : null,
                      p.awaitingReply > 0 ? `${p.awaitingReply} sem resposta` : null,
                      p.unassigned > 0 ? `${p.unassigned} sem responsável` : null,
                      p.stale > 0 ? `${p.stale} parado${p.stale > 1 ? 's' : ''}` : null,
                    ].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          ))}
          {ranked.length > MAX_PIPELINE_CARDS && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              +{ranked.length - MAX_PIPELINE_CARDS} pipeline{ranked.length - MAX_PIPELINE_CARDS > 1 ? 's' : ''} no painel do CRM
            </Typography>
          )}

          <Box sx={{ mt: 'auto' }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 1 }}>
              {newLast7Days} novo{newLast7Days === 1 ? '' : 's'} em 7 dias · {wonLast30Days} ganho{wonLast30Days === 1 ? '' : 's'} em 30 dias
            </Typography>
            <Button onClick={onOpen} variant="contained" fullWidth sx={{ ...cardButtonSx, backgroundColor: '#0d6e6e', '&:hover': { backgroundColor: '#0a5757' } }}>
              Ver pipelines e agir →
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// Baldes de status da agenda (status_display). Feriado/recesso da clínica ficam fora no backend.
type BucketKey = 'compareceu' | 'naoCompareceu' | 'canceladoPaciente' | 'canceladoProfissional' | 'aguardando';
const STATUS_BUCKETS: { key: BucketKey; label: string; color: string }[] = [
  { key: 'compareceu', label: 'Compareceu', color: '#28a745' },
  { key: 'naoCompareceu', label: 'Não compareceu', color: '#dc3545' },
  { key: 'canceladoPaciente', label: 'Cancelado paciente', color: '#fd7e14' },
  { key: 'canceladoProfissional', label: 'Cancelado profissional', color: '#6f42c1' },
  { key: 'aguardando', label: 'Aguardando', color: '#6c757d' },
];

const Dashboard: React.FC = () => {
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  // Default = 30 dias móveis (consistente com os cards "Atendimentos (30d)" e cancelamentos 30d).
  const getInitialStartDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  };
  const getInitialEndDate = () => new Date().toISOString().split('T')[0];
  const initialStartDate = getInitialStartDate();
  const initialEndDate = getInitialEndDate();

  // Caixa de aniversários do Dashboard: 3 dias para trás e 3 para frente (pedido do Hilton,
  // 11/09/2026). A janela curta é o que dá para AGIR — parabenizar quem faz hoje, correr atrás de
  // quem passou nos últimos dias e preparar os próximos. A lista completa, com período livre,
  // mora na página Aniversariantes (RN-524).
  const JANELA_ANIVERSARIO_DIAS = 3;
  // O card tem a altura dos vizinhos: mais que isto empurra a linha inteira e parece corte.
  const NA_CAIXA = 4;
  const diaRelativo = (dias: number): string => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dias);
    return d.toISOString().split('T')[0];
  };
  const getBdayStart = () => diaRelativo(-JANELA_ANIVERSARIO_DIAS);
  // Pede um mês à frente, mas EXIBE só os ±3 dias. A sobra é a reserva: quando não há ninguém na
  // janela curta, o card mostra os próximos em vez de uma caixa vazia (pedido do Hilton, 11/09).
  const getBdayEnd = () => diaRelativo(30);
  const initialBdayStart = getBdayStart();
  const initialBdayEnd = getBdayEnd();

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [exporting, setExporting] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  // Planilha única (RN-550): cancelamentos 15d, maiores 30d, risco de evasão e presença por dia do período
  const exportXlsx = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await dashboardService.exportXlsx(startDate, endDate);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dashboard_cancelamentos_${startDate}_${endDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Não foi possível exportar a planilha.", "error");
    } finally {
      setExporting(false);
    }
  };
  const bdayStart = initialBdayStart;
  const bdayEnd = initialBdayEnd;

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await dashboardService.summary(startDate, endDate, bdayStart, bdayEnd));
    } catch {
      setError("Não foi possível carregar os indicadores do dashboard.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, bdayStart, bdayEnd]);

  useEffect(() => {
    document.title = "Clinic4Us - Dashboard";
    const sessionData = localStorage.getItem('clinic4us-user-session');
    if (sessionData) {
      try {
        setUserSession(JSON.parse(sessionData));
      } catch {
        redirectToLogin();
      }
    } else {
      redirectToLogin();
    }
  }, []);

  useEffect(() => {
    if (userSession) {
      loadSummary();
    }
  }, [userSession, loadSummary]);

  const handleRevalidateLogin = () => {
    localStorage.removeItem('clinic4us-user-session');
    localStorage.removeItem('clinic4us-remember-me');
    redirectToLogin();
  };

  const handleClearFilters = () => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  };



  // cada widget avisa quando nao tem dado/permissao; a linha vira coluna unica
  const [opportunitiesOk, setOpportunitiesOk] = useState(true);
  const [pipelinesOk, setPipelinesOk] = useState(true);
  const hideOpportunities = useCallback(() => setOpportunitiesOk(false), []);
  const hidePipelines = useCallback(() => setPipelinesOk(false), []);

  const { user: authUser } = useAuth();
  // Link "Ver relatório" só aparece para quem tem a página no perfil
  const reportPageIfAllowed = (page: string): string | undefined =>
    (authUser?.allowedPages || []).includes(page) ? page : undefined;

  const goTo = (page: string) => {
    const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
    window.location.href = `${window.location.origin}/?page=${page}&clinic=${clinic}`;
  };
  /**
   * Aniversariantes do card: os ±3 dias em torno de hoje. Quando não há ninguém aí, cai para os
   * PRÓXIMOS do mês — caixa vazia não ajuda ninguém, e quem olha o card quer saber quem
   * parabenizar em seguida.
   */
  const aniversariantes = React.useMemo(() => {
    const lista = summary?.birthdays?.lista ?? [];
    // `birth` chega em dd/MM/yyyy (não ISO): comparar por fatia de string dava "78//1" na tela e
    // um recorte de janela sem sentido. Aqui vira dia-do-ano para caber na janela de ±3 dias.
    const hoje = new Date();
    const diaDoAno = (d: Date) => Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000);
    const hojeNo = diaDoAno(hoje);
    const distancia = (b: { birth: string }): number => {
      const [dia, mes] = b.birth.split('/').map((v) => parseInt(v, 10));
      if (!dia || !mes) return 999;
      const diff = diaDoAno(new Date(hoje.getFullYear(), mes - 1, dia)) - hojeNo;
      // aniversário já passado neste ano conta como o do ano que vem
      return diff < -JANELA_ANIVERSARIO_DIAS ? diff + 365 : diff;
    };

    const naJanela = lista.filter((b) => Math.abs(distancia(b)) <= JANELA_ANIVERSARIO_DIAS);
    const ordenada = [...lista].sort((a, b) => distancia(a) - distancia(b));
    return naJanela.length > 0
      ? { lista: naJanela.slice(0, NA_CAIXA), futuro: false }
      : { lista: ordenada.filter((b) => distancia(b) > 0).slice(0, NA_CAIXA), futuro: lista.length > 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  const goToBirthdays = () => {
    const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
    window.location.href = `${window.location.origin}/?page=birthdays&clinic=${clinic}`;
  };

  /**
   * O cadastro do paciente em NOVA ABA, para a fila de evoluções (RN-216): o terapeuta abre um
   * paciente, evolui e volta para a lista ainda aberta. Na mesma aba ele perderia o painel a cada
   * atendimento e teria de recarregar o dashboard inteiro.
   */
  const goToPatientNewTab = (patientId: string) => {
    const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
    window.open(`${window.location.origin}/?page=patient-register&id=${patientId}&clinic=${clinic}`, '_blank');
  };

  const goToPatient = (patientId: string) => {
    const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
    window.location.href = `${window.location.origin}/?page=patient-register&id=${patientId}&clinic=${clinic}`;
  };
  const scrollTo = (selector: string) => document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' });


  if (!userSession) {
    return (
      <Box className="login-page">
        <Box className="dashboard-loading">Carregando...</Box>
      </Box>
    );
  }

  const appt = summary?.appointmentsToday;
  const att = summary?.attendances30d;
  const reg = summary?.patientRegistrations;
  const regBars = reg
    ? [
        { label: 'Com evolução', value: reg.comEvolucao, color: '#17a2b8' },
        { label: 'Com agendamentos', value: reg.comAgendamentos, color: '#28a745' },
        { label: 'Cadastro completo', value: reg.cadastroCompleto, color: '#ffc107' },
        { label: 'Com plano de ação', value: reg.comPlanoAcao, color: '#fd7e14' },
        { label: 'Com CID', value: reg.comCid, color: '#e83e8c' },
      ]
    : [];
  const regMax = Math.max(1, ...regBars.map((b) => b.value));
  const dailyGoal = summary?.dailyAttendanceGoal ?? null;
  const presenceMax = Math.max(1, dailyGoal ?? 0, ...(summary?.presenceByDay ?? []).map((d) => STATUS_BUCKETS.reduce((s, b) => s + d[b.key], 0)));

  return (
    <Box className="login-page">
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={userSession.email}
        userProfile={userSession.role}
        clinicName={userSession.clinicName}
        notificationCount={0}
        onRevalidateLogin={handleRevalidateLogin}
        onLogoClick={() => window.location.reload()}
      />

      <Box className="dashboard-main">
        <Container className="dashboard-container" maxWidth={false} disableGutters>
          <Box className="dashboard-content">

            <Box className="c4u-page-header c4u-page-header--in-panel">
              <Box className="c4u-page-header__texts">
                <Typography variant="h4" className="c4u-page-header__title">Dashboard</Typography>
                <Typography variant="body2" className="c4u-page-header__subtitle">
                  Visão geral dos principais indicadores e estatísticas da clínica.
                </Typography>
              </Box>
              <FaqButton />
            </Box>

            {/* Painel único (decisão do Hilton, 05/09/2026): os 4 cards, o bloco de ação
                (oportunidades + captação), o grupo de cancelamentos/evasão e a presença por dia
                moram no MESMO container do título; a lista de aniversariantes virou página. */}
            <div className="c4u-panel dashboard-panel">
            {error && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8d7da', color: '#842029', borderRadius: '8px' }}>
                <Typography variant="body2">{error}</Typography>
              </Paper>
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 6 }}>
                <CircularProgress size={22} sx={{ color: '#03B4C6' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Carregando indicadores…</Typography>
              </Box>
            )}

            {!loading && summary && (
            <>
            {/* Para o PROFISSIONAL, "Minhas evoluções" abre o dashboard (pedido do Hilton,
                22/09/2026): é a única fila de trabalho dele nesta tela — os cards do topo
                (compromissos, atendimentos, cadastros, aniversários) são leitura de gestão. Nos
                demais perfis o painel segue depois dos cards, onde sempre esteve.
                O critério é o escopo do dado, não o nome do perfil: `own` é quem só enxerga o
                próprio atendimento — mesma régua já usada aqui para esconder oportunidades e
                captação do terapeuta. */}
            {summary.scope === 'own' && (
              <MyEvolutionsWidget onOpenPatient={goToPatientNewTab} first />
            )}

            {/* Cards de Estatísticas */}
            <Box className="dashboard-stats-grid">
              {/* Compromissos hoje */}
              <Box>
                <Paper className="dashboard-card" elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important' }}>
                  <Box className="dashboard-card-header">
                    <Box className="dashboard-card-icon blue"><CalendarToday /></Box>
                    <Typography variant="h6" className="dashboard-card-title" sx={{ fontSize: '1rem' }}>Compromissos hoje</Typography>
                  </Box>
                  <Box className="dashboard-card-content">
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Donut centerValue={appt!.total} segments={STATUS_BUCKETS.map((b) => ({ value: appt![b.key], color: b.color }))} />
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {STATUS_BUCKETS.map((b) => <LegendRow key={b.key} color={b.color} label={b.label} value={appt![b.key]} />)}
                      </Box>
                    </Box>
                    {(summary.nonSessionToday ?? 0) > 0 && (
                      <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: -1 }}>
                        + {summary.nonSessionToday} sem cobrança por sessão (reserva/aula) — fora do volume
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={() => goTo('schedule')} className="dashboard-card-button" variant="contained" fullWidth sx={pairButtonSx}>VER AGENDA</Button>
                    {(authUser?.allowedPages || []).includes('day-schedule') && (
                      <Button onClick={() => goTo('day-schedule')} className="dashboard-card-button" variant="outlined" fullWidth sx={{ ...pairButtonSx, backgroundColor: 'transparent', color: colors.primary, borderColor: colors.primary, '&:hover': { backgroundColor: colors.primaryLight, borderColor: colors.primary } }}>AGENDA DIA</Button>
                    )}
                  </Box>
                </Paper>
              </Box>

              {/* Atendimentos (30d) */}
              <Box>
                <Paper className="dashboard-card" elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important' }}>
                  <Box className="dashboard-card-header">
                    <Box className="dashboard-card-icon green"><CheckCircle /></Box>
                    <Typography variant="h6" className="dashboard-card-title" sx={{ fontSize: '1rem' }}>Atendimentos (30d)</Typography>
                  </Box>
                  <Box className="dashboard-card-content">
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Donut centerValue={att!.total} segments={STATUS_BUCKETS.map((b) => ({ value: att![b.key], color: b.color }))} />
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {STATUS_BUCKETS.map((b) => <LegendRow key={b.key} color={b.color} label={b.label} value={att![b.key]} />)}
                      </Box>
                    </Box>
                    {(summary.nonSession30d ?? 0) > 0 && (
                      <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: -1 }}>
                        + {summary.nonSession30d} sem cobrança por sessão (reserva/aula) — fora do volume
                      </Typography>
                    )}
                  </Box>
                  <Button onClick={() => scrollTo('.dashboard-attendance-chart')} className="dashboard-card-button" variant="contained" fullWidth sx={cardButtonSx}>VER DADOS</Button>
                </Paper>
              </Box>

              {/* Registros de pacientes */}
              <Box>
                <Paper className="dashboard-card" elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important' }}>
                  <Box className="dashboard-card-header">
                    <Box className="dashboard-card-icon orange"><Assignment /></Box>
                    <Typography variant="h6" className="dashboard-card-title" sx={{ fontSize: '1rem' }}>Registros de pacientes</Typography>
                  </Box>
                  <Box className="dashboard-card-content">
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                      Pacientes com agendamento no mês atual
                    </Typography>
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {regBars.map((bar) => (
                        <Box key={bar.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, minWidth: '110px', textAlign: 'right' }}>{bar.label}</Typography>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                              <Box sx={{ width: `${(bar.value / regMax) * 100}%`, height: '100%', backgroundColor: bar.color }} />
                            </Box>
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: bar.color, minWidth: '30px' }}>{bar.value}</Typography>
                        </Box>
                      ))}
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total de pacientes</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2196f3' }}>{reg!.total}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Button onClick={() => goTo('patients')} className="dashboard-card-button" variant="contained" fullWidth sx={cardButtonSx}>VER PACIENTES</Button>
                </Paper>
              </Box>

              {/* Aniversários */}
              <Box>
                <Paper className="dashboard-card" elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important' }}>
                  <Box className="dashboard-card-header">
                    <Box className="dashboard-card-icon pink"><Cake /></Box>
                    <Typography variant="h6" className="dashboard-card-title" sx={{ fontSize: '1rem' }}>Aniversários</Typography>
                  </Box>
                  <Box className="dashboard-card-content">
                    {/* Sem donut: "quantos fazem aniversário no mês" não leva a nenhuma ação.
                        O que serve é a lista curta de quem faz agora — 3 dias para cada lado. */}
                    {aniversariantes.lista.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                        Nenhum aniversário nos próximos 30 dias.
                      </Typography>
                    ) : (
                      <>
                      {aniversariantes.futuro && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                          Ninguém nos últimos 3 dias — mostrando os próximos
                        </Typography>
                      )}
                      {aniversariantes.lista.map((b) => (
                        <Box
                          key={b.patientId}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid #f1f3f5' }}
                        >
                          <Typography sx={{ fontSize: '0.875rem', color: '#212529', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                            {titleCaseName(b.name)}
                            {b.age != null && (
                              <Typography component="span" sx={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                {' '}· {b.age} {b.age === 1 ? 'ano' : 'anos'}
                              </Typography>
                            )}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: b.isToday ? 700 : 400,
                              color: b.isToday ? colors.primary : '#6c757d',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {b.isToday ? 'hoje' : b.birth.slice(0, 5)}
                          </Typography>
                        </Box>
                      ))}
                      </>
                    )}
                  </Box>
                  {/* Só aparece para quem tem a página: antes levava todo mundo, e quem não tinha
                      o grant caía em "acesso restrito" (achado do Hilton, 11/09). */}
                  {reportPageIfAllowed('birthdays') && (
                    <Button onClick={goToBirthdays} className="dashboard-card-button" variant="contained" fullWidth sx={cardButtonSx}>VER ANIVERSÁRIOS</Button>
                  )}
                </Paper>
              </Box>
            </Box>

            {/* Minhas evoluções (RN-216). NÃO é condicionado ao perfil: a Diretoria tem
                data_scope 'entity' e mesmo assim atende pacientes — escondendo por perfil, quem
                atende e coordena nunca via a própria fila. Quem decide é o SERVIDOR, pelo vínculo
                de atendimento: sem UEPR o painel volta vazio e o componente não renderiza nada.
                Aqui é a posição de QUEM NÃO É `own`; o Profissional já viu o painel no topo. */}
            {summary.scope !== 'own' && (
              <MyEvolutionsWidget onOpenPatient={goToPatientNewTab} />
            )}

            {/* Oportunidades da agenda e pipeline do CRM dividem a linha: os dois são "o que
                precisa de ação agora". Quando um deles não está disponível para o perfil, o
                outro assume a linha inteira em vez de deixar meia tela vazia. */}
            {/* Perfil com escopo 'own' (Profissional) não vê os dois: oportunidades e captação são
                trabalho da secretaria/coordenação, não do terapeuta (Hilton, 05/09). */}
            {summary?.scope !== 'own' && (opportunitiesOk || pipelinesOk) && (
            <Box className="dashboard-group" sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: opportunitiesOk && pipelinesOk ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' },
                gap: 3,
                alignItems: 'stretch',
              }}
            >
              <OpportunitiesWidget
                onOpen={() => goTo('opportunities')}
                onOpenRepositions={() => {
                  const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
                  window.location.href = `${window.location.origin}/?page=opportunities&tab=repositions&clinic=${clinic}`;
                }}
                onUnavailable={hideOpportunities}
              />
              <PipelineWidget
                onOpen={() => goTo('crm-funnel')}
                onOpenPipeline={(pipelineId) => {
                  const clinic = new URLSearchParams(window.location.search).get('clinic') || 'ninho';
                  window.location.href = `${window.location.origin}/?page=crm-funnel&pipelineId=${pipelineId}&clinic=${clinic}`;
                }}
                onUnavailable={hidePipelines}
              />
            </Box>
            </Box>
            )}

            {/* Cancelamentos e evasão (RN-524), no modelo de duas colunas: a esquerda é o que
                pede ação AGORA (o que já caiu dos próximos 15 dias e quem parou de vir), a
                direita é a leitura do mês. Antes eram dois painéis de cancelamento lado a lado
                com a evasão embaixo, e a repetição escondia que respondem a perguntas diferentes. */}
            <Box className="dashboard-group" sx={{ mt: 3 }}>
              <CancellationsSection
                next15d={summary.cancellationsNext15d ?? summary.cancellationsToday ?? []}
                last30d={summary.cancellations30d ?? []}
                atRisk={summary.patientsAtRisk ?? []}
                onOpenPatient={goToPatient}
                onExport={summary.scope !== 'own' ? exportXlsx : undefined}
                exporting={exporting}
                cancellationsReportPage={reportPageIfAllowed('report-cancellations')}
                patientFlowReportPage={reportPageIfAllowed('report-patient-flow')}
                onGoTo={goTo}
                podeContatar={summary.scope !== 'own'}
              />
            </Box>

            {/* Status de Presença por Dia */}
            <Box className="dashboard-attendance-chart" sx={{ mt: 3 }}>
              <Paper elevation={0} sx={{ boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important', p: 3, borderRadius: '12px' }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box className="dashboard-card-icon blue"><BarChart /></Box>
                    <Typography variant="h5" sx={{ fontSize: '1.25rem' }}>Status de Presença por Dia</Typography>
                  </Box>
                  <DateRangeFilter startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onClear={handleClearFilters} initialStartDate={initialStartDate} initialEndDate={initialEndDate} />
                </Box>

                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: '340px', borderBottom: '2px solid #e9ecef', borderLeft: '2px solid #e9ecef', pl: 2, pb: 2, overflowX: 'auto' }}>
                    {(summary.presenceByDay ?? []).map((d, i) => {
                      const maxHeight = 280;
                      const total = STATUS_BUCKETS.reduce((s, b) => s + d[b.key], 0);
                      const seg = (v: number, c: string, tip: string) => v > 0 ? (
                        <Tooltip title={`${tip}: ${v}`} arrow key={c}>
                          <Box sx={{ width: '35px', height: `${(v / presenceMax) * maxHeight}px`, backgroundColor: c, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
                        </Tooltip>
                      ) : null;
                      return (
                        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column-reverse', height: `${maxHeight}px`, justifyContent: 'flex-start', gap: 0.5 }}>
                            {STATUS_BUCKETS.map((b) => seg(d[b.key], b.color, b.label))}
                            {/* Total de sessões do dia (todas as cores); o detalhe por status fica no tooltip de cada segmento */}
                            {total > 0 && (
                              <Typography key="num" variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.primary', textAlign: 'center', lineHeight: 1 }}>{total}</Typography>
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{d.data}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {dailyGoal != null && dailyGoal > 0 && (
                    <Box sx={{ position: 'absolute', left: '16px', right: 0, bottom: `${16 + (dailyGoal / presenceMax) * 280}px`, borderTop: '2px dashed #03B4C6', pointerEvents: 'none', zIndex: 2 }}>
                      <Typography variant="caption" sx={{ position: 'absolute', right: 4, top: '-18px', color: '#03B4C6', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.9)', px: 0.5, borderRadius: '4px' }}>
                        Meta: {dailyGoal} atend./dia
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 3, mt: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {STATUS_BUCKETS.map((b) => (
                    <Box key={b.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: b.color, borderRadius: '4px' }} />
                      <Typography variant="body2">{b.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>

            </>
            )}
            </div>
          </Box>
        </Container>
      </Box>

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default Dashboard;
